import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Tooltip,
  Alert,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Functions as FunctionsIcon,
  FilterAlt as FilterIcon,
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  CheckCircleOutline as ValidIcon,
  WarningAmber as WarningIcon,
  PlaylistAddCheck as SchemaIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../../store';
import { ProcessNode, CalculatedColumn } from '../../types/report';
import EmployeeFilter, { FilterGroup, FilterCondition, JoinOp } from '../EmployeeFilter';

interface ProcessConfigModalProps {
  open: boolean;
  node: ProcessNode | null;
  parentId: string;
  parentName?: string;
  pipelineTree?: ProcessNode[];
  onSave: (node: ProcessNode) => void;
  onClose: () => void;
}

// ── Canonical Details Fields Definition ──────────────────────────────────────

interface DetailFieldDef {
  name: string;
  label: string;
  type: 'time' | 'number' | 'boolean' | 'date' | 'string';
}

const CANONICAL_DETAIL_FIELDS: DetailFieldDef[] = [
  { name: 'inTime', label: 'In Time (HH:mm)', type: 'time' },
  { name: 'outTime', label: 'Out Time (HH:mm)', type: 'time' },
  { name: 'isLate', label: 'Is Late', type: 'boolean' },
  { name: 'LateMinute', label: 'Late (Minutes)', type: 'number' },
  { name: 'isAbsence', label: 'Is Absence', type: 'boolean' },
  { name: 'isOnLeave', label: 'Is On Leave', type: 'boolean' },
  { name: 'LeaveType', label: 'Leave Type', type: 'string' },
  { name: 'Date', label: 'Date (YYYY-MM-DD)', type: 'date' },
  { name: 'shiftName', label: 'Shift Name', type: 'string' },
  { name: 'workStartTime', label: 'Work Start Time', type: 'time' },
  { name: 'workEndTime', label: 'Work End Time', type: 'time' },
  { name: 'isHoliday', label: 'Is Holiday', type: 'boolean' },
  { name: 'isWeeklyOff', label: 'Is Weekly Off', type: 'boolean' },
  { name: 'PunchError', label: 'Punch Error', type: 'boolean' },
  { name: 'isPresentInHoliday', label: 'Present In Holiday', type: 'boolean' },
];

const DETAIL_OPS: Record<string, { label: string; needsVal: boolean }> = {
  equals: { label: 'Equals (=)', needsVal: true },
  not_equals: { label: 'Not Equals (!=)', needsVal: true },
  gt: { label: 'Greater Than (>)', needsVal: true },
  gte: { label: 'Greater / Equal (>=)', needsVal: true },
  lt: { label: 'Less Than (<)', needsVal: true },
  lte: { label: 'Less / Equal (<=)', needsVal: true },
  contains: { label: 'Contains', needsVal: true },
  not_contains: { label: 'Not Contains', needsVal: true },
  starts_with: { label: 'Starts With', needsVal: true },
  ends_with: { label: 'Ends With', needsVal: true },
  has_value: { label: 'Has Value (Not Empty)', needsVal: false },
  is_empty: { label: 'Is Empty', needsVal: false },
};

function getOpsForDetailField(type: string): string[] {
  if (type === 'boolean') return ['equals', 'not_equals'];
  if (type === 'number') return ['gt', 'gte', 'lt', 'lte', 'equals', 'not_equals'];
  if (type === 'time' || type === 'date') return ['equals', 'not_equals', 'gt', 'lt', 'has_value', 'is_empty'];
  return ['contains', 'not_contains', 'equals', 'not_equals', 'starts_with', 'ends_with', 'has_value', 'is_empty'];
}

// ── Formula Syntax Quick Validator ──────────────────────────────────────────

function validateFormulaSyntax(formula: string): { valid: boolean; message: string } {
  const trimmed = formula.trim();
  if (!trimmed) return { valid: true, message: 'Formula is empty' };

  let parenBalance = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === '(') parenBalance++;
    else if (trimmed[i] === ')') parenBalance--;
    if (parenBalance < 0) return { valid: false, message: 'Unmatched closing parenthesis ")"' };
  }
  if (parenBalance > 0) return { valid: false, message: `Missing ${parenBalance} closing parenthesis ")"` };

  return { valid: true, message: 'Syntax balanced' };
}

// ── Main Component ──────────────────────────────────────────────────────────

export const ProcessConfigModal: React.FC<ProcessConfigModalProps> = ({
  open,
  node,
  parentId,
  parentName,
  pipelineTree = [],
  onSave,
  onClose,
}) => {
  const uiFields = useAppSelector((state) => state.applicationSettings.uiFields);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [keepEmptyEmployees, setKeepEmptyEmployees] = useState(false);
  const [mainTab, setMainTab] = useState(0); // 0 = Filters, 1 = Calculations
  const [filterSubTab, setFilterSubTab] = useState(0); // 0 = Main Filters, 1 = Details Filters
  const [calcSubTab, setCalcSubTab] = useState(0); // 0 = Main Calculations, 1 = Details Calculations

  const [mainFilters, setMainFilters] = useState<FilterGroup[]>([]);
  const [detailsFilters, setDetailsFilters] = useState<FilterGroup[]>([]);
  const [mainCalculations, setMainCalculations] = useState<CalculatedColumn[]>([]);
  const [detailsCalculations, setDetailsCalculations] = useState<CalculatedColumn[]>([]);

  const [activeFormulaIdx, setActiveFormulaIdx] = useState<number>(0);
  const [schemaSearch, setSchemaSearch] = useState('');
  const [tipsTab, setTipsTab] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);

  // Initialize form state
  useEffect(() => {
    if (open) {
      if (node) {
        setName(node.name || '');
        setDescription(node.description || '');
        setKeepEmptyEmployees(!!node.keepEmptyEmployees);
        setMainFilters(node.mainFilters || []);
        setDetailsFilters(node.detailsFilters || []);
        setMainCalculations(node.mainCalculations || []);
        setDetailsCalculations(node.detailsCalculations || []);
      } else {
        setName('');
        setDescription('');
        setKeepEmptyEmployees(false);
        setMainFilters([]);
        setDetailsFilters([]);
        setMainCalculations([]);
        setDetailsCalculations([]);
      }
      setMainTab(0);
      setFilterSubTab(0);
      setCalcSubTab(0);
      setActiveFormulaIdx(0);
      setFormError(null);
    }
  }, [open, node]);

  // ── Inherited Schema Resolution ───────────────────────────────────────────
  const inheritedSchema = useMemo(() => {
    const mainCols: Array<{ name: string; label: string; type: string; source: string }> = [
      { name: 'internalId', label: 'Employee ID', type: 'string', source: 'Base Data' },
      { name: 'name', label: 'Employee Name', type: 'string', source: 'Base Data' },
      { name: 'joiningDate', label: 'Joining Date', type: 'date', source: 'Base Data' },
      { name: 'resignationDate', label: 'Resignation Date', type: 'date', source: 'Base Data' },
      { name: 'employeeUUID', label: 'Employee UUID', type: 'string', source: 'Base Data' },
    ];

    // Add active UI profile fields
    uiFields
      .filter((f) => f.active)
      .forEach((f) => {
        mainCols.push({
          name: f.name,
          label: f.title || f.name,
          type: f.inputType === 'DATE_PICKER' ? 'date' : 'string',
          source: 'Profile Attribute',
        });
      });

    const detailsCols: Array<{ name: string; label: string; type: string; source: string }> =
      CANONICAL_DETAIL_FIELDS.map((d) => ({
        name: d.name,
        label: d.label,
        type: d.type,
        source: 'Base Data',
      }));

    // Trace ancestors along pipelineTree to inherit calculated metrics
    if (pipelineTree.length > 0 && parentId && parentId !== 'base-data') {
      let currentId: string | undefined = parentId;
      const ancestors: ProcessNode[] = [];
      const visited = new Set<string>();

      while (currentId && currentId !== 'base-data' && currentId !== '0' && !visited.has(currentId)) {
        visited.add(currentId);
        const searchId: string = currentId;
        const parent = pipelineTree.find((n) => n.id === searchId);
        if (!parent) break;
        ancestors.unshift(parent);
        currentId = parent.parentId;
      }

      ancestors.forEach((anc) => {
        anc.mainCalculations?.forEach((c) => {
          mainCols.push({
            name: c.name,
            label: c.name,
            type: c.dataType || 'number',
            source: `${anc.name} (Calculated)`,
          });
        });
        anc.detailsCalculations?.forEach((c) => {
          detailsCols.push({
            name: c.name,
            label: c.name,
            type: c.dataType || 'number',
            source: `${anc.name} (Calculated)`,
          });
        });
      });
    }

    return { mainCols, detailsCols };
  }, [uiFields, pipelineTree, parentId]);

  // Details Filter Group Handlers
  const addDetailsGroup = () => {
    const newGroup: FilterGroup = {
      id: 'grp_' + Date.now().toString(36),
      join: 'AND',
      logic: 'AND',
      conditions: [
        {
          id: 'c_' + Date.now().toString(36),
          fieldName: 'isLate',
          operator: 'equals',
          value: 'true',
          enabled: true,
        },
      ],
    };
    setDetailsFilters((prev) => [...prev, newGroup]);
  };

  const removeDetailsGroup = (gid: string) => {
    setDetailsFilters((prev) => prev.filter((g) => g.id !== gid));
  };

  const addDetailsCondition = (gid: string) => {
    setDetailsFilters((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        return {
          ...g,
          conditions: [
            ...g.conditions,
            {
              id: 'c_' + Date.now().toString(36),
              fieldName: 'isLate',
              operator: 'equals',
              value: 'true',
              enabled: true,
            },
          ],
        };
      })
    );
  };

  const removeDetailsCondition = (gid: string, cid: string) => {
    setDetailsFilters((prev) =>
      prev
        .map((g) => {
          if (g.id !== gid) return g;
          return { ...g, conditions: g.conditions.filter((c) => c.id !== cid) };
        })
        .filter((g) => g.conditions.length > 0)
    );
  };

  const updateDetailsCondition = (gid: string, cid: string, patch: Partial<FilterCondition>) => {
    setDetailsFilters((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        return {
          ...g,
          conditions: g.conditions.map((c) => (c.id === cid ? { ...c, ...patch } : c)),
        };
      })
    );
  };

  // Calculations Helpers
  const handleAddCalculation = (isDetails: boolean) => {
    const newCol: CalculatedColumn = {
      name: isDetails ? 'customDetail' + (detailsCalculations.length + 1) : 'customMain' + (mainCalculations.length + 1),
      formula: '',
      dataType: 'number',
    };
    if (isDetails) {
      setDetailsCalculations((prev) => [...prev, newCol]);
      setActiveFormulaIdx(detailsCalculations.length);
    } else {
      setMainCalculations((prev) => [...prev, newCol]);
      setActiveFormulaIdx(mainCalculations.length);
    }
  };

  const handleUpdateCalculation = (index: number, field: keyof CalculatedColumn, val: any, isDetails: boolean) => {
    if (isDetails) {
      setDetailsCalculations((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: val };
        return next;
      });
    } else {
      setMainCalculations((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: val };
        return next;
      });
    }
  };

  const handleRemoveCalculation = (index: number, isDetails: boolean) => {
    if (isDetails) {
      setDetailsCalculations((prev) => prev.filter((_, i) => i !== index));
    } else {
      setMainCalculations((prev) => prev.filter((_, i) => i !== index));
    }
    setActiveFormulaIdx(0);
  };

  const insertTokenIntoActiveFormula = (token: string, isDetails: boolean) => {
    const targetList = isDetails ? detailsCalculations : mainCalculations;
    if (targetList.length === 0) return;
    const idx = Math.min(activeFormulaIdx, targetList.length - 1);
    const curr = targetList[idx]?.formula || '';
    const updated = curr ? (curr.endsWith(' ') || curr.endsWith('(') ? curr + token : curr + ' ' + token) : token;
    handleUpdateCalculation(idx, 'formula', updated, isDetails);
  };

  const handleSave = () => {
    if (!name.trim()) {
      setFormError('Process name is required.');
      return;
    }

    const checkNames = (cols: CalculatedColumn[]) => {
      const names = new Set<string>();
      for (const col of cols) {
        const cName = col.name.trim();
        if (!cName) return 'Calculated column name cannot be empty.';
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(cName)) {
          return `Column name "${cName}" must contain only letters, numbers, and underscores.`;
        }
        if (names.has(cName)) {
          return `Duplicate calculated column name: "${cName}".`;
        }
        names.add(cName);
      }
      return null;
    };

    const mainErr = checkNames(mainCalculations);
    if (mainErr) {
      setFormError(mainErr);
      return;
    }
    const detailsErr = checkNames(detailsCalculations);
    if (detailsErr) {
      setFormError(detailsErr);
      return;
    }

    const updatedNode: ProcessNode = {
      id: node ? node.id : 'proc_' + Date.now().toString(36),
      parentId: node ? node.parentId : parentId,
      name: name.trim(),
      description: description.trim(),
      keepEmptyEmployees,
      mainFilters,
      detailsFilters,
      mainCalculations,
      detailsCalculations,
    };

    onSave(updatedNode);
  };

  // Filtered schema columns for search
  const filteredMainCols = useMemo(() => {
    if (!schemaSearch.trim()) return inheritedSchema.mainCols;
    const q = schemaSearch.toLowerCase();
    return inheritedSchema.mainCols.filter((c) => c.name.toLowerCase().includes(q) || c.label.toLowerCase().includes(q));
  }, [inheritedSchema.mainCols, schemaSearch]);

  const filteredDetailsCols = useMemo(() => {
    if (!schemaSearch.trim()) return inheritedSchema.detailsCols;
    const q = schemaSearch.toLowerCase();
    return inheritedSchema.detailsCols.filter((c) => c.name.toLowerCase().includes(q) || c.label.toLowerCase().includes(q));
  }, [inheritedSchema.detailsCols, schemaSearch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {node ? `Edit Process: ${node.name}` : 'Configure New Child Process'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Inheriting from Parent Node: <strong>{parentName || (parentId === 'base-data' ? 'Base Data (Root)' : parentId)}</strong>
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 520, p: { xs: 2, sm: 3 } }}>
        {formError && <Alert severity="error">{formError}</Alert>}

        {/* Process Meta Header */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            required
            label="Process Name"
            placeholder="e.g. Late Deduction Audit"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ flex: 1.5, minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Description (Optional)"
            placeholder="e.g. Filters late employees and aggregates penalty hours"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ flex: 2, minWidth: 260 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={keepEmptyEmployees}
                onChange={(e) => setKeepEmptyEmployees(e.target.checked)}
                size="small"
              />
            }
            label={
              <Tooltip title="If checked, employees are preserved in output even if all daily records are pruned by Details filters.">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Keep Empty Employees</Typography>
                  <HelpIcon fontSize="inherit" color="action" />
                </Box>
              </Tooltip>
            }
            sx={{ flexShrink: 0 }}
          />
        </Box>

        {/* Main Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={mainTab} onChange={(_, val) => setMainTab(val)}>
            <Tab icon={<FilterIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Filter Rules" />
            <Tab icon={<FunctionsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Calculated Columns" />
          </Tabs>
        </Box>

        {/* TAB 0: FILTER RULES */}
        {mainTab === 0 && (
          <Box>
            <Tabs
              value={filterSubTab}
              onChange={(_, val) => setFilterSubTab(val)}
              sx={{ minHeight: 36, mb: 2 }}
            >
              <Tab label="Main Filters (Employee Level)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem', fontWeight: 600 }} />
              <Tab label="Details Filters (Daily Records)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem', fontWeight: 600 }} />
            </Tabs>

            {/* Sub-tab 0: Main Filters (Using EmployeeFilter exactly like Data Autopsy) */}
            {filterSubTab === 0 && (
              <Box>
                <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Filter Employees
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select departments, tags, or employee fields to filter which employees enter this process branch.
                  </Typography>
                  <EmployeeFilter
                    uiFields={uiFields}
                    value={mainFilters}
                    onGroupsChange={(updated) => setMainFilters(updated)}
                  />
                </Paper>
              </Box>
            )}

            {/* Sub-tab 1: Details Filters (Daily Records) */}
            {filterSubTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Filter daily attendance records. Records that do not match are pruned from the daily records array.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addDetailsGroup}
                    sx={{ textTransform: 'none' }}
                  >
                    Add Filter Group
                  </Button>
                </Box>

                {detailsFilters.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No daily record filters configured. All attendance days from parent will be passed through.
                    </Typography>
                  </Paper>
                ) : (
                  detailsFilters.map((group, gIdx) => (
                    <Paper key={group.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {gIdx > 0 && (
                            <Select
                              size="small"
                              value={group.join}
                              onChange={(e) => {
                                const next = detailsFilters.map((g) => (g.id === group.id ? { ...g, join: e.target.value as JoinOp } : g));
                                setDetailsFilters(next);
                              }}
                              sx={{ minWidth: 75, height: 26, fontSize: '0.72rem', fontWeight: 700 }}
                            >
                              <MenuItem value="AND">AND</MenuItem>
                              <MenuItem value="OR">OR</MenuItem>
                            </Select>
                          )}
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Match
                          </Typography>
                          <Select
                            size="small"
                            value={group.logic}
                            onChange={(e) => {
                              const next = detailsFilters.map((g) => (g.id === group.id ? { ...g, logic: e.target.value as JoinOp } : g));
                              setDetailsFilters(next);
                            }}
                            sx={{ minWidth: 80, height: 26, fontSize: '0.72rem', fontWeight: 700, color: 'primary.main' }}
                          >
                            <MenuItem value="AND">ALL conditions</MenuItem>
                            <MenuItem value="OR">ANY condition</MenuItem>
                          </Select>
                          <Typography variant="caption" color="text.secondary">
                            of:
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => removeDetailsGroup(group.id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      {/* Conditions list */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {group.conditions.map((cond) => {
                          const fieldDef = CANONICAL_DETAIL_FIELDS.find((d) => d.name === cond.fieldName);
                          const fieldType = fieldDef?.type || 'string';
                          const ops = getOpsForDetailField(fieldType);
                          const needsVal = DETAIL_OPS[cond.operator]?.needsVal !== false;
                          const isBool = fieldType === 'boolean';

                          return (
                            <Box key={cond.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: cond.enabled ? 1 : 0.45 }}>
                              <Checkbox
                                size="small"
                                checked={cond.enabled}
                                onChange={(e) => updateDetailsCondition(group.id, cond.id, { enabled: e.target.checked })}
                                sx={{ p: 0.5 }}
                              />
                              <Select
                                size="small"
                                value={cond.fieldName}
                                onChange={(e) => {
                                  const fName = e.target.value;
                                  const def = CANONICAL_DETAIL_FIELDS.find((d) => d.name === fName);
                                  const defType = def?.type || 'string';
                                  const defOps = getOpsForDetailField(defType);
                                  updateDetailsCondition(group.id, cond.id, {
                                    fieldName: fName,
                                    operator: defOps[0],
                                    value: defType === 'boolean' ? 'true' : '',
                                  });
                                }}
                                sx={{ width: 180, fontSize: '0.78rem' }}
                              >
                                {CANONICAL_DETAIL_FIELDS.map((d) => (
                                  <MenuItem key={d.name} value={d.name} sx={{ fontSize: '0.78rem' }}>
                                    {d.label}
                                  </MenuItem>
                                ))}
                              </Select>

                              <Select
                                size="small"
                                value={cond.operator}
                                onChange={(e) => updateDetailsCondition(group.id, cond.id, { operator: e.target.value })}
                                sx={{ width: 160, fontSize: '0.78rem' }}
                              >
                                {ops.map((opKey) => (
                                  <MenuItem key={opKey} value={opKey} sx={{ fontSize: '0.78rem' }}>
                                    {DETAIL_OPS[opKey]?.label ?? opKey}
                                  </MenuItem>
                                ))}
                              </Select>

                              {needsVal && (
                                isBool ? (
                                  <Select
                                    size="small"
                                    value={cond.value || 'true'}
                                    onChange={(e) => updateDetailsCondition(group.id, cond.id, { value: e.target.value })}
                                    sx={{ width: 140, fontSize: '0.78rem' }}
                                  >
                                    <MenuItem value="true" sx={{ fontSize: '0.78rem' }}>True</MenuItem>
                                    <MenuItem value="false" sx={{ fontSize: '0.78rem' }}>False</MenuItem>
                                  </Select>
                                ) : (
                                  <TextField
                                    size="small"
                                    placeholder="Value"
                                    value={cond.value}
                                    onChange={(e) => updateDetailsCondition(group.id, cond.id, { value: e.target.value })}
                                    sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.6 } }}
                                  />
                                )
                              )}

                              <IconButton size="small" onClick={() => removeDetailsCondition(group.id, cond.id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          );
                        })}
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => addDetailsCondition(group.id)}
                          sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                        >
                          Add condition
                        </Button>
                      </Box>
                    </Paper>
                  ))
                )}
              </Box>
            )}
          </Box>
        )}

        {/* TAB 1: CALCULATED COLUMNS */}
        {mainTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Tabs
              value={calcSubTab}
              onChange={(_, val) => {
                setCalcSubTab(val);
                setActiveFormulaIdx(0);
              }}
              sx={{ minHeight: 36, mb: 1 }}
            >
              <Tab label="Main Calculations (Aggregations over Details)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem', fontWeight: 600 }} />
              <Tab label="Details Calculations (Row Expressions per Day)" sx={{ minHeight: 36, py: 0.5, fontSize: '0.82rem', fontWeight: 600 }} />
            </Tabs>

            {/* Calculations List */}
            {(() => {
              const isDetails = calcSubTab === 1;
              const list = isDetails ? detailsCalculations : mainCalculations;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {isDetails
                        ? 'Create custom daily metrics calculated on each row (e.g. isLateNew, overtimeHours).'
                        : 'Create employee-level attributes aggregated across the period (e.g. SUM(Details.LateMinute), COUNT(Details.inTime)).'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddCalculation(isDetails)}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Column
                    </Button>
                  </Box>

                  {list.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No calculated columns defined. Click &quot;Add Column&quot; to create derived metrics.
                      </Typography>
                    </Paper>
                  ) : (
                    list.map((col, idx) => {
                      const validation = validateFormulaSyntax(col.formula);
                      const isSelected = activeFormulaIdx === idx;

                      return (
                        <Paper
                          key={idx}
                          variant="outlined"
                          onClick={() => setActiveFormulaIdx(idx)}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: isSelected ? '2px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            bgcolor: isSelected ? 'primary.50' : 'background.paper',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <TextField
                              size="small"
                              label="Column Name"
                              value={col.name}
                              onChange={(e) => handleUpdateCalculation(idx, 'name', e.target.value, isDetails)}
                              sx={{ width: 170, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
                            />
                            <FormControl size="small" sx={{ width: 120 }}>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={col.dataType}
                                label="Type"
                                onChange={(e) => handleUpdateCalculation(idx, 'dataType', e.target.value, isDetails)}
                                sx={{ fontSize: '0.82rem' }}
                              >
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="string">String</MenuItem>
                                <MenuItem value="boolean">Boolean</MenuItem>
                              </Select>
                            </FormControl>

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={1}
                                maxRows={8}
                                label="Formula Expression"
                                value={col.formula}
                                placeholder={
                                  isDetails
                                    ? 'e.g. IF(Details.LateMinute > 15, true, false)'
                                    : 'e.g. SUM(Details.LateMinute) / 60'
                                }
                                onChange={(e) => handleUpdateCalculation(idx, 'formula', e.target.value, isDetails)}
                                onFocus={() => setActiveFormulaIdx(idx)}
                                sx={{
                                  '& .MuiInputBase-input': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    color: 'primary.dark',
                                    fontWeight: 500,
                                    lineHeight: 1.5,
                                  },
                                }}
                              />
                              {col.formula.trim() && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
                                  {validation.valid ? (
                                    <ValidIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                  ) : (
                                    <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.7rem',
                                      color: validation.valid ? 'success.dark' : 'warning.dark',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {validation.message}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCalculation(idx, isDetails)}
                              sx={{ mt: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Paper>
                      );
                    })
                  )}

                  {/* ── INHERITED COLUMNS INSPECTOR ────────────────────────────── */}
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fbfbfb', mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchemaIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={700}>
                          Inherited Columns from Parent
                        </Typography>
                        <Chip
                          label={parentName || (parentId === 'base-data' ? 'Base Data (Root)' : parentId)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                        />
                      </Box>
                      <TextField
                        size="small"
                        placeholder="Search columns..."
                        value={schemaSearch}
                        onChange={(e) => setSchemaSearch(e.target.value)}
                        sx={{ width: 180, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.4 } }}
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Click any column below to insert it directly into the active formula expression:
                    </Typography>

                    {/* Main Employee Columns */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
                        MAIN COLUMNS (Employee Level):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', maxHeight: 110, overflowY: 'auto' }}>
                        {filteredMainCols.map((c) => (
                          <Chip
                            key={c.name}
                            label={`+ Main.${c.name}`}
                            size="small"
                            onClick={() => insertTokenIntoActiveFormula(`Main.${c.name}`, isDetails)}
                            sx={{
                              fontSize: '0.72rem',
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              bgcolor: 'background.paper',
                              border: '1px solid #d0d7de',
                              '&:hover': { bgcolor: 'primary.light', color: '#fff' },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Details Columns */}
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ display: 'block', mb: 0.5 }}>
                        DETAILS COLUMNS (Daily Record Level):
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', maxHeight: 110, overflowY: 'auto' }}>
                        {filteredDetailsCols.map((c) => (
                          <Chip
                            key={c.name}
                            label={`+ Details.${c.name}`}
                            size="small"
                            onClick={() => insertTokenIntoActiveFormula(`Details.${c.name}`, isDetails)}
                            sx={{
                              fontSize: '0.72rem',
                              fontFamily: 'monospace',
                              cursor: 'pointer',
                              bgcolor: 'background.paper',
                              border: '1px solid #d0d7de',
                              '&:hover': { bgcolor: 'secondary.light', color: '#fff' },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Paper>

                  {/* ── FORMULA ASSISTANT & CODING TIPS ────────────────────────── */}
                  <Accordion variant="outlined" sx={{ borderRadius: 2, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CodeIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={700}>
                          Formula Assistant & Function Cheat Sheet
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Tabs
                        value={tipsTab}
                        onChange={(_, val) => setTipsTab(val)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ minHeight: 32, mb: 1.5, borderBottom: 1, borderColor: 'divider' }}
                      >
                        <Tab label="Aggregations" sx={{ minHeight: 32, py: 0.3, fontSize: '0.75rem', fontWeight: 600 }} />
                        <Tab label="Conditionals" sx={{ minHeight: 32, py: 0.3, fontSize: '0.75rem', fontWeight: 600 }} />
                        <Tab label="Time & Date" sx={{ minHeight: 32, py: 0.3, fontSize: '0.75rem', fontWeight: 600 }} />
                        <Tab label="Math & Numbers" sx={{ minHeight: 32, py: 0.3, fontSize: '0.75rem', fontWeight: 600 }} />
                        <Tab label="String Operations" sx={{ minHeight: 32, py: 0.3, fontSize: '0.75rem', fontWeight: 600 }} />
                        <Tab label="1-Click Recipes" sx={{ minHeight: 32, py: 0.3, fontSize: '0.75rem', fontWeight: 600 }} />
                      </Tabs>

                      {/* 1. Aggregations */}
                      {tipsTab === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Used in Main Calculations to aggregate over all daily attendance records for an employee:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              { label: 'COUNT inTime', code: 'COUNT(Details.inTime)' },
                              { label: 'COUNT isLate', code: 'COUNT(Details.isLate == true)' },
                              { label: 'SUM LateMinute', code: 'SUM(Details.LateMinute)' },
                              { label: 'AVG LateMinute', code: 'AVG(Details.LateMinute)' },
                              { label: 'MAX LateMinute', code: 'MAX(Details.LateMinute)' },
                              { label: 'FIRST inTime', code: 'FIRST(Details.inTime)' },
                              { label: 'LAST outTime', code: 'LAST(Details.outTime)' },
                              { label: 'EXACT_DATE', code: "EXACT_DATE(Details.inTime, '2026-10-01')" },
                            ].map((s) => (
                              <Button
                                key={s.label}
                                size="small"
                                variant="outlined"
                                onClick={() => insertTokenIntoActiveFormula(s.code, isDetails)}
                                sx={{ textTransform: 'none', fontFamily: 'monospace', fontSize: '0.72rem', py: 0.2 }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* 2. Conditionals */}
                      {tipsTab === 1 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Conditional branching logic (supports standard IF function or block DSL):
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              { label: 'IF(cond, true, false)', code: 'IF(Details.LateMinute > 15, "Late", "Normal")' },
                              { label: 'IF Penalty', code: 'IF(SUM(Details.LateMinute) > 60, true, false)' },
                              { label: 'IF Absence', code: 'IF(Details.isAbsence, 0, 1)' },
                              { label: 'Block IF DSL', code: 'IF Details.LateMinute > 15 THEN true ELSE false END IF' },
                            ].map((s) => (
                              <Button
                                key={s.label}
                                size="small"
                                variant="outlined"
                                onClick={() => insertTokenIntoActiveFormula(s.code, isDetails)}
                                sx={{ textTransform: 'none', fontFamily: 'monospace', fontSize: '0.72rem', py: 0.2 }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* 3. Time & Date */}
                      {tipsTab === 2 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Calculate durations, differences, and calendar details:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              { label: 'TIME_DIFF (min)', code: "TIME_DIFF(Details.outTime, Details.inTime, 'm')" },
                              { label: 'TIME_DIFF (hours)', code: "TIME_DIFF(Details.outTime, Details.inTime, 'h')" },
                              { label: 'DATE_DIFF (days)', code: "DATE_DIFF(Details.Date, Main.joiningDate, 'days')" },
                              { label: 'ADD_DAYS', code: 'ADD_DAYS(Main.joiningDate, 90)' },
                              { label: 'DAY_OF_WEEK', code: 'DAY_OF_WEEK(Details.Date)' },
                              { label: 'IS_WEEKEND', code: 'IS_WEEKEND(Details.Date)' },
                              { label: 'EXTRACT_HOUR', code: 'EXTRACT_HOUR(Details.inTime)' },
                              { label: 'EXTRACT_MINUTE', code: 'EXTRACT_MINUTE(Details.inTime)' },
                            ].map((s) => (
                              <Button
                                key={s.label}
                                size="small"
                                variant="outlined"
                                onClick={() => insertTokenIntoActiveFormula(s.code, isDetails)}
                                sx={{ textTransform: 'none', fontFamily: 'monospace', fontSize: '0.72rem', py: 0.2 }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* 4. Math */}
                      {tipsTab === 3 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Mathematical transformations and rounding:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              { label: 'ROUND', code: 'ROUND(val, 2)' },
                              { label: 'CEIL', code: 'CEIL(val / 15)' },
                              { label: 'FLOOR', code: 'FLOOR(val / 15)' },
                              { label: 'ABS', code: 'ABS(val)' },
                              { label: 'MOD', code: 'MOD(val, 2)' },
                              { label: 'CLAMP', code: 'CLAMP(val, 0, 100)' },
                            ].map((s) => (
                              <Button
                                key={s.label}
                                size="small"
                                variant="outlined"
                                onClick={() => insertTokenIntoActiveFormula(s.code, isDetails)}
                                sx={{ textTransform: 'none', fontFamily: 'monospace', fontSize: '0.72rem', py: 0.2 }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* 5. Strings */}
                      {tipsTab === 4 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            String formatting and text manipulation:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              { label: 'CONCAT', code: 'CONCAT(Main.name, " (", Main.internalId, ")")' },
                              { label: 'UPPER', code: 'UPPER(Main.name)' },
                              { label: 'LOWER', code: 'LOWER(Details.shiftName)' },
                              { label: 'TRIM', code: 'TRIM(Main.name)' },
                              { label: 'SUBSTR', code: 'SUBSTR(Main.internalId, 0, 3)' },
                              { label: 'REPLACE', code: 'REPLACE(Main.internalId, "0", "")' },
                            ].map((s) => (
                              <Button
                                key={s.label}
                                size="small"
                                variant="outlined"
                                onClick={() => insertTokenIntoActiveFormula(s.code, isDetails)}
                                sx={{ textTransform: 'none', fontFamily: 'monospace', fontSize: '0.72rem', py: 0.2 }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* 6. Recipes */}
                      {tipsTab === 5 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Ready-to-use production formulas (click to insert):
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {[
                              {
                                label: 'Overtime Hours (>8h worked)',
                                code: "IF(TIME_DIFF(Details.outTime, Details.inTime, 'h') > 8, TIME_DIFF(Details.outTime, Details.inTime, 'h') - 8, 0)",
                              },
                              {
                                label: 'Severe Late Penalty Flag (>15m)',
                                code: 'IF(Details.LateMinute > 15, true, false)',
                              },
                              {
                                label: 'Total Hours Worked Sum',
                                code: "ROUND(SUM(TIME_DIFF(Details.outTime, Details.inTime, 'm')) / 60, 2)",
                              },
                              {
                                label: 'Perfect Attendance Flag',
                                code: 'IF(SUM(Details.LateMinute) == 0 AND COUNT(Details.isAbsence == true) == 0, true, false)',
                              },
                            ].map((s) => (
                              <Button
                                key={s.label}
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={() => insertTokenIntoActiveFormula(s.code, isDetails)}
                                sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0.3 }}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })()}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessConfigModal;
